/**
 * Swing-speed-aware gear-spec generator for comparison testing.
 * Seeds loft, flex, weight, launch, and head type ranges from the player's
 * measured club speed so every config is realistic for that golfer.
 */

import type { GearSpecs, ShaftFlex, IronCategory, BallCover, LaunchProfile, HeadType, WeightSetting } from '../equipment/types';

// ── Speed bracket definitions ────────────────────────────────────────────────
// A real fitter narrows the search space based on club speed before testing.

interface SpeedBracket {
  label: string;
  loft:        { min: number; max: number };
  shaftWeight: { min: number; max: number };
  flex:        ShaftFlex[];
  launch:      LaunchProfile[];
  headTypes:   HeadType[];
  weightSettings: WeightSetting[];
  ironCategories: IronCategory[];
  clubLength:  { min: number; max: number };
}

/** Return the speed bracket for a given driver club speed (mph). */
function getSpeedBracket(clubSpeed: number): SpeedBracket {
  if (clubSpeed < 85) {
    // Slow — senior / beginner / women
    return {
      label: 'slow',
      loft:        { min: 10.5, max: 13 },
      shaftWeight: { min: 40,   max: 55 },
      flex:        ['L', 'A', 'R'],
      launch:      ['mid', 'mid-high', 'high'],
      headTypes:   ['max-forgiveness', 'draw', 'standard'],
      weightSettings: ['standard', 'draw', 'back'],
      ironCategories: ['game-improvement', 'players-distance'],
      clubLength:  { min: 45.5, max: 46.5 },
    };
  }
  if (clubSpeed < 95) {
    // Moderate — average recreational golfer
    return {
      label: 'moderate',
      loft:        { min: 9.5,  max: 12 },
      shaftWeight: { min: 50,   max: 70 },
      flex:        ['R', 'S'],
      launch:      ['mid-low', 'mid', 'mid-high'],
      headTypes:   ['standard', 'draw', 'max-forgiveness'],
      weightSettings: ['standard', 'draw', 'back', 'front'],
      ironCategories: ['game-improvement', 'players-distance', 'players'],
      clubLength:  { min: 45, max: 46 },
    };
  }
  if (clubSpeed < 105) {
    // Fast — low handicap / competitive
    return {
      label: 'fast',
      loft:        { min: 8.5,  max: 10.5 },
      shaftWeight: { min: 55,   max: 75 },
      flex:        ['S', 'X'],
      launch:      ['low', 'mid-low', 'mid'],
      headTypes:   ['standard', 'low-spin', 'fade'],
      weightSettings: ['standard', 'draw', 'fade', 'front'],
      ironCategories: ['players', 'players-distance', 'blade'],
      clubLength:  { min: 44.5, max: 45.5 },
    };
  }
  // Tour — 105+ mph
  return {
    label: 'tour',
    loft:        { min: 8,    max: 10 },
    shaftWeight: { min: 60,   max: 80 },
    flex:        ['S', 'X'],
    launch:      ['low', 'mid-low'],
    headTypes:   ['standard', 'low-spin', 'fade'],
    weightSettings: ['standard', 'front', 'fade'],
    ironCategories: ['blade', 'players'],
    clubLength:  { min: 44, max: 45.5 },
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function randomInRange(min: number, max: number, step: number): number {
  const steps = Math.round((max - min) / step);
  return min + Math.round(Math.random() * steps) * step;
}

function randomChoice<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Compute a correlated swing weight from club length and shaft weight. */
function correlatedSwingWeight(clubLength: number, shaftWeight: number): string {
  const base = (clubLength - 44) * 2 + (shaftWeight - 50) * 0.03;
  const idx = Math.max(0, Math.min(6, Math.round(base + Math.random() * 2)));
  return `D${idx}`;
}

/** Shuffle array in-place (Fisher-Yates). */
function shuffleArray<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ── Speed-aware generators ───────────────────────────────────────────────────

/** Generate a spec seeded to the player's speed bracket. */
function generateBracketSpec(bracket: SpeedBracket): GearSpecs {
  const shaftWeight = randomInRange(bracket.shaftWeight.min, bracket.shaftWeight.max, 5);
  const shaftFlex = randomChoice(bracket.flex);
  const clubLength = randomInRange(bracket.clubLength.min, bracket.clubLength.max, 0.5);
  return {
    loft: randomInRange(bracket.loft.min, bracket.loft.max, 0.5),
    shaftWeight,
    shaftFlex,
    ballCompression: 90,
    ballCover: 'urethane' as BallCover,
    ironCategory: randomChoice(bracket.ironCategories),
    clubLength,
    swingWeight: correlatedSwingWeight(clubLength, shaftWeight),
    launchProfile: randomChoice(bracket.launch),
    headType: randomChoice(bracket.headTypes),
    weightSetting: randomChoice(bracket.weightSettings),
  };
}

/** "Optimized" spec — tighter ranges around the sweet spot for this speed. */
function generateOptimizedSpec(bracket: SpeedBracket): GearSpecs {
  // Narrow to inner 60% of bracket ranges
  const loftRange = bracket.loft.max - bracket.loft.min;
  const loftMin = bracket.loft.min + loftRange * 0.2;
  const loftMax = bracket.loft.max - loftRange * 0.2;

  const wRange = bracket.shaftWeight.max - bracket.shaftWeight.min;
  const wMin = bracket.shaftWeight.min + Math.round(wRange * 0.2 / 5) * 5;
  const wMax = bracket.shaftWeight.max - Math.round(wRange * 0.2 / 5) * 5;

  const shaftWeight = randomInRange(wMin, wMax, 5);
  const shaftFlex = randomChoice(bracket.flex);
  const clubLength = randomInRange(bracket.clubLength.min, bracket.clubLength.max, 0.5);

  // Prefer lower-launch for faster swingers, higher for slower
  const launchOptions = bracket.launch.length > 1
    ? bracket.launch.slice(0, Math.max(2, bracket.launch.length - 1))
    : bracket.launch;

  return {
    loft: randomInRange(Math.round(loftMin * 2) / 2, Math.round(loftMax * 2) / 2, 0.5),
    shaftWeight,
    shaftFlex,
    ballCompression: 90,
    ballCover: 'urethane' as BallCover,
    ironCategory: randomChoice(bracket.ironCategories.slice(0, 2)),
    clubLength,
    swingWeight: correlatedSwingWeight(clubLength, shaftWeight),
    launchProfile: randomChoice(launchOptions) as LaunchProfile,
    headType: randomChoice(bracket.headTypes.slice(0, 2)),
    weightSetting: randomChoice(bracket.weightSettings.slice(0, 2)),
  };
}

/** "Exploratory" spec — pushes toward edges of the bracket to find surprises. */
function generateExploratorySpec(bracket: SpeedBracket): GearSpecs {
  // Use full bracket range but allow one step outside in each direction
  const loftMin = Math.max(8, bracket.loft.min - 0.5);
  const loftMax = Math.min(13, bracket.loft.max + 0.5);
  const wMin = Math.max(40, bracket.shaftWeight.min - 5);
  const wMax = Math.min(85, bracket.shaftWeight.max + 5);

  const shaftWeight = randomInRange(wMin, wMax, 5);
  const shaftFlex = randomChoice(bracket.flex);
  const clubLength = randomInRange(
    Math.max(44, bracket.clubLength.min - 0.5),
    Math.min(46.5, bracket.clubLength.max + 0.5),
    0.5,
  );

  // Allow any launch/head from the bracket
  const ALL_HEADS: HeadType[] = ['standard', 'draw', 'fade', 'low-spin', 'max-forgiveness'];
  const ALL_LAUNCH: LaunchProfile[] = ['low', 'mid-low', 'mid', 'mid-high', 'high'];

  return {
    loft: randomInRange(loftMin, loftMax, 0.5),
    shaftWeight,
    shaftFlex,
    ballCompression: 90,
    ballCover: 'urethane' as BallCover,
    ironCategory: randomChoice(bracket.ironCategories),
    clubLength,
    swingWeight: correlatedSwingWeight(clubLength, shaftWeight),
    launchProfile: randomChoice(ALL_LAUNCH),
    headType: randomChoice(ALL_HEADS),
    weightSetting: randomChoice(bracket.weightSettings),
  };
}

/** "Mismatched" spec — one bracket off from ideal to show the performance drop-off.
 *  Deliberately stays within plausible range (no L-flex for tour players). */
function generateMismatchedSpec(bracket: SpeedBracket): GearSpecs {
  const isSlowBracket = bracket.label === 'slow' || bracket.label === 'moderate';

  // One bracket heavier/lighter — wrong but not absurd
  const shaftWeight = isSlowBracket
    ? randomInRange(70, 80, 5)    // one step too heavy
    : randomInRange(45, 55, 5);   // one step too light

  // One flex step off — R for tour/fast, S for slow
  const shaftFlex: ShaftFlex = isSlowBracket
    ? randomChoice(['S', 'X'] as ShaftFlex[])   // too stiff
    : randomChoice(['R', 'R'] as ShaftFlex[]);   // too flexible (but not absurdly so)

  const loft = isSlowBracket
    ? randomInRange(8.5, 9.5, 0.5)   // too low
    : randomInRange(11, 12, 0.5);    // too high

  const clubLength = isSlowBracket
    ? randomChoice([44, 44.5])
    : randomChoice([46, 46.5]);

  return {
    loft,
    shaftWeight,
    shaftFlex,
    ballCompression: 90,
    ballCover: 'urethane' as BallCover,
    ironCategory: isSlowBracket
      ? randomChoice(['blade', 'players'] as IronCategory[])
      : randomChoice(['players-distance', 'game-improvement'] as IronCategory[]),
    clubLength,
    swingWeight: correlatedSwingWeight(clubLength, shaftWeight),
    launchProfile: isSlowBracket
      ? randomChoice(['low', 'mid-low'] as LaunchProfile[])
      : randomChoice(['high', 'mid-high'] as LaunchProfile[]),
    headType: isSlowBracket
      ? randomChoice(['low-spin', 'standard'] as HeadType[])
      : randomChoice(['max-forgiveness', 'draw'] as HeadType[]),
    weightSetting: randomChoice(['front', 'fade', 'draw'] as WeightSetting[]),
  };
}

// ── ELDRICK Neighborhood Generator ──────────────────────────────────────────

/** Flex ordering for stepping up/down. */
const FLEX_ORDER: ShaftFlex[] = ['L', 'A', 'R', 'S', 'X'];

interface NeighborhoodAnchor {
  loft: number;
  shaftWeight: number;
  shaftFlex: string;
  compression: number;
  details?: Partial<GearSpecs>;
}

/**
 * Generate specs around an ELDRICK recommendation anchor.
 * Config #1 is always the anchor itself ("ELDRICK Rec").
 * Remaining configs vary 1–2 parameters at a time with deterministic offsets.
 */
export function generateNeighborhoodSpecs(
  anchor: NeighborhoodAnchor,
  count: number,
  clubKey: string,
  _clubSpeed?: number,
): GearSpecs[] {
  const iron = clubKey !== 'driver' && clubKey !== '3wood';
  const loftBounds: [number, number] = iron ? [18, 65] : [8, 13];
  const weightBounds: [number, number] = iron ? [40, 140] : [40, 85];

  // Build the anchor as a full GearSpecs (fills in defaults for fields not in the recommendation)
  const anchorSpec: GearSpecs = {
    loft: anchor.loft,
    shaftWeight: anchor.shaftWeight,
    shaftFlex: (anchor.shaftFlex as ShaftFlex) || 'S',
    ballCompression: anchor.compression,
    ballCover: 'urethane' as BallCover,
    ironCategory: 'players',
    clubLength: iron ? 37 : 45.5,
    swingWeight: 'D3',
    launchProfile: 'mid' as LaunchProfile,
    headType: 'standard' as HeadType,
    weightSetting: 'standard' as WeightSetting,
    ...anchor.details,
  };

  const specs: GearSpecs[] = [anchorSpec]; // #1 is always the anchor
  const used = new Set<string>();
  used.add(specKey(anchorSpec));

  // Define single-parameter offsets
  // Iron loft is not modeled by the spec engine; don't manufacture comparisons.
  const loftOffsets = iron ? [] : [-1.0, -0.5, 0.5, 1.0];
  const weightOffsets = [-10, -5, 5, 10];
  const flexIdx = FLEX_ORDER.indexOf(anchorSpec.shaftFlex as ShaftFlex);

  // Single-param variations: loft
  for (const dLoft of loftOffsets) {
    if (specs.length >= count) break;
    const s = { ...anchorSpec, loft: clamp(anchorSpec.loft + dLoft, ...loftBounds) };
    addUnique(s);
  }

  // Single-param: shaft weight
  for (const dw of weightOffsets) {
    if (specs.length >= count) break;
    const s = { ...anchorSpec, shaftWeight: clamp(anchorSpec.shaftWeight + dw, ...weightBounds) };
    addUnique(s);
  }

  // Single-param: flex
  for (const step of [-1, 1]) {
    if (specs.length >= count) break;
    const newIdx = flexIdx + step;
    if (newIdx >= 0 && newIdx < FLEX_ORDER.length) {
      const s = { ...anchorSpec, shaftFlex: FLEX_ORDER[newIdx] };
      addUnique(s);
    }
  }

  // Two-param combos: loft + weight
  for (const dLoft of iron ? [] : [-0.5, 0.5]) {
    for (const dw of [-5, 5]) {
      if (specs.length >= count) break;
      const s = {
        ...anchorSpec,
        loft: clamp(anchorSpec.loft + dLoft, ...loftBounds),
        shaftWeight: clamp(anchorSpec.shaftWeight + dw, ...weightBounds),
      };
      addUnique(s);
    }
  }

  // Two-param combos: loft + flex
  for (const dLoft of iron ? [] : [-0.5, 0.5]) {
    for (const step of [-1, 1]) {
      if (specs.length >= count) break;
      const newIdx = flexIdx + step;
      if (newIdx >= 0 && newIdx < FLEX_ORDER.length) {
        const s = {
          ...anchorSpec,
          loft: clamp(anchorSpec.loft + dLoft, ...loftBounds),
          shaftFlex: FLEX_ORDER[newIdx],
        };
        addUnique(s);
      }
    }
  }

  // Two-param combos: shaft weight + flex. Ball changes belong to the ball phase.
  for (const dw of [-5, 5]) {
    for (const step of [-1, 1]) {
      if (specs.length >= count) break;
      const flex = FLEX_ORDER[flexIdx + step];
      if (!flex) continue;
      const s = {
        ...anchorSpec,
        shaftWeight: clamp(anchorSpec.shaftWeight + dw, ...weightBounds),
        shaftFlex: flex,
      };
      addUnique(s);
    }
  }

  for (const delta of [-0.5, -0.25, 0.25, 0.5]) {
    addUnique({ ...anchorSpec, clubLength: clamp(anchorSpec.clubLength + delta, iron ? 35 : 43, iron ? 40 : 47) });
  }
  for (const launchProfile of ['low', 'mid-low', 'mid', 'mid-high', 'high'] as LaunchProfile[]) {
    addUnique({ ...anchorSpec, launchProfile });
  }
  if (iron) for (const ironCategory of ['blade', 'players', 'players-distance', 'game-improvement'] as IronCategory[]) {
    addUnique({ ...anchorSpec, ironCategory });
  }

  // Finite, reproducible neighborhood. Never pad with random, unrelated setups.
  return specs.slice(0, Math.max(0, Math.floor(count)));

  function specKey(s: GearSpecs): string {
    return JSON.stringify(s);
  }

  function clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
  }

  function addUnique(s: GearSpecs): void {
    const k = specKey(s);
    if (!used.has(k) && specs.length < count) {
      specs.push(s);
      used.add(k);
    }
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Generate a single random GearSpecs seeded to the player's speed.
 *  Ball specs fixed to defaults — ball is selected in its own step. */
export function generateRandomSpecs(clubSpeed?: number): GearSpecs {
  const bracket = getSpeedBracket(clubSpeed ?? 95);
  return generateBracketSpec(bracket);
}

/** Generate a set of N random GearSpecs for comparison testing. */
export function generateRandomSpecSet(count: number, clubSpeed?: number): GearSpecs[] {
  return Array.from({ length: count }, () => generateRandomSpecs(clubSpeed));
}

/**
 * Generate a set of specs with intentional spread, all seeded to the player's
 * measured club speed.  Distribution:
 *   ~25% optimized (sweet spot for this speed)
 *   ~35% bracket  (full range appropriate for speed)
 *   ~20% exploratory (edge of bracket, looking for surprises)
 *   ~20% mismatched  (wrong bracket — shows what to avoid)
 * Shuffled so tiers aren't visually grouped.
 */
export function generateSpreadSpecSet(count: number, clubSpeed?: number): GearSpecs[] {
  const bracket = getSpeedBracket(clubSpeed ?? 95);
  const specs: GearSpecs[] = [];

  const goodCount = Math.round(count * 0.25);
  const bracketCount = Math.round(count * 0.35);
  const exploratoryCount = Math.round(count * 0.20);
  const mismatchCount = count - goodCount - bracketCount - exploratoryCount;

  for (let i = 0; i < goodCount; i++) specs.push(generateOptimizedSpec(bracket));
  for (let i = 0; i < bracketCount; i++) specs.push(generateBracketSpec(bracket));
  for (let i = 0; i < exploratoryCount; i++) specs.push(generateExploratorySpec(bracket));
  for (let i = 0; i < mismatchCount; i++) specs.push(generateMismatchedSpec(bracket));

  return shuffleArray(specs);
}
